export interface WhatsAppStatus {
  connected: boolean;
  state: 'open' | 'connecting' | 'close' | 'disconnected' | 'error' | string;
  instance_name: string;
  phone_number?: string | null;
  profile_name?: string | null;
  profile_picture_url?: string | null;
  connected_at?: string | null;
  message?: string | null;
}

export interface WhatsAppConnectResult {
  connected: boolean;
  state: string;
  phone_number?: string | null;
  qrcode_base64?: string | null;
  pairing_code?: string | null;
  message?: string | null;
}

export interface WhatsAppDisconnectResult {
  success: boolean;
  message: string;
}

const API_BASE_URL = '';

export async function fetchWhatsAppStatus(
  token: string
): Promise<WhatsAppStatus> {
  const response = await fetch(`${API_BASE_URL}/api/whatsapp/status`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    throw new Error(
      `Falha ao consultar status do WhatsApp (${response.status})`
    );
  }

  return response.json();
}

export async function requestWhatsAppConnect(
  token: string
): Promise<WhatsAppConnectResult> {
  const response = await fetch(`${API_BASE_URL}/api/whatsapp/connect`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `Erro ao solicitar pareamento do WhatsApp (${response.status})`
    );
  }

  return response.json();
}

export async function requestWhatsAppDisconnect(
  token: string
): Promise<WhatsAppDisconnectResult> {
  const response = await fetch(`${API_BASE_URL}/api/whatsapp/disconnect`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Erro ao desconectar WhatsApp (${response.status})`);
  }

  return response.json();
}

export interface WhatsAppSessionEvent {
  id: string;
  timestamp: string;
  event: string;
  details: string;
  status: 'online' | 'offline' | 'warning';
}

export async function fetchWhatsAppEvents(
  token: string
): Promise<WhatsAppSessionEvent[]> {
  const response = await fetch(`${API_BASE_URL}/api/whatsapp/events`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    return [];
  }

  return response.json();
}

export async function logWhatsAppEvent(
  token: string,
  event: {
    event: string;
    details: string;
    status: 'online' | 'offline' | 'warning';
  }
): Promise<WhatsAppSessionEvent | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/whatsapp/events`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });

    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

export interface WhatsAppInstance {
  id: string;
  tenant_id: string;
  instance_name: string;
  phone_number?: string | null;
  status: 'CONNECTED' | 'DISCONNECTED' | 'CONNECTING' | string;
  battery_level?: number | null;
  daily_sent_count: number;
  daily_quota: number;
  last_heartbeat_at?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface CreateWhatsAppInstancePayload {
  instance_name?: string;
  daily_quota?: number;
}

export async function fetchWhatsAppInstances(
  token: string
): Promise<WhatsAppInstance[]> {
  const response = await fetch(`${API_BASE_URL}/api/whatsapp/instances`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    throw new Error(`Falha ao listar chips do WhatsApp (${response.status})`);
  }

  return response.json();
}

export async function createWhatsAppInstance(
  token: string,
  payload: CreateWhatsAppInstancePayload
): Promise<WhatsAppInstance> {
  const response = await fetch(`${API_BASE_URL}/api/whatsapp/instances`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Falha ao cadastrar novo chip (${response.status})`);
  }

  return response.json();
}

export async function connectWhatsAppInstance(
  token: string,
  instanceId: string
): Promise<WhatsAppConnectResult> {
  const response = await fetch(
    `${API_BASE_URL}/api/whatsapp/instances/${instanceId}/connect`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Erro ao conectar chip (${response.status})`);
  }

  return response.json();
}

export async function disconnectWhatsAppInstance(
  token: string,
  instanceId: string
): Promise<WhatsAppDisconnectResult> {
  const response = await fetch(
    `${API_BASE_URL}/api/whatsapp/instances/${instanceId}/disconnect`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Erro ao desconectar chip (${response.status})`);
  }

  return response.json();
}

export async function deleteWhatsAppInstance(
  token: string,
  instanceId: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/whatsapp/instances/${instanceId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Erro ao remover chip (${response.status})`);
  }

  return response.json();
}
